import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { roleTemplate } from "../lib/roleTemplate";
import { useLanguage } from "../context/LanguageContext";
import {
  LayoutDashboard,
  Users,
  TrendingUp,
  MessageSquare,
  BarChart3,
  LogOut,
  Phone,
  Menu,
  X,
  ShieldCheck,
  Plug,
  Contact,
  CalendarClock,
  Zap,
  Package,
  UserCircle,
  Building2,
  Smartphone,
  ClipboardList,
  FileText,
  Mail,
  UsersRound,
  Award,
  Briefcase,
  CalendarCheck,
  UserPlus,
  CalendarDays,
  Warehouse,
  HardHat,
  Factory,
  Settings,
  Trash2,
  Megaphone,
  ClipboardCheck,
  Clock,
  Facebook,
  IndianRupee,
  Package as PackageIcon,
  Megaphone as MegaphoneIcon,
  FolderOpen,
  LogOut as OffboardingIcon,
  Wallet,
  Link2,
  Activity,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { NotificationBell } from "../components/NotificationBell";
import { PushNotificationPrompt } from "../components/PushNotificationPrompt";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sales-pipeline-hub-24/artifacts/mmegsjtv_Ameenji.logo%20Black%20.png";

// Permission key for each nav route
const routePermissionMap = {
  "/dashboard": null, // Always visible — DashboardRouter renders the right view per role/dept
  "/leads": "leads",
  "/contacts": "contacts",
  "/sales-pipeline": "sales_pipeline",
  "/products": "products",
  "/companies": "companies",
  "/tasks": "tasks",
  "/work-reports": "work_reports",
  "/teams": "teams",
  "/performance": "performance",
  "/follow-ups": "follow_ups",
  "/activities": "activities",
  "/analytics": "analytics",
  "/reports": "reports",
  "/email-templates": "email_templates",
  "/chat": "chat",
  "/users": "staff_management",
  "/integrations": "integrations",
  "/my-leaves": "my_leaves",
  "/profile": null, // Always visible
  "/hr/employees": "hr_employees",
  "/hr/attendance": "hr_attendance",
  "/hr/leaves": "hr_leaves",
  "/hr/leave-calendar": "hr_leaves",
  "/hr/payroll": "hr_payroll",
  "/hr/hiring": "hr_hiring",
  "/hr/onboarding": "hr_employees",
  "/hr/onboarding/settings": "hr_employees",
  "/attendance/checkin": "mobile_checkin",
  "/hr/settings": "hr_employees",
  "/quick-actions": "dashboard",
  "/infrastructure": "infrastructure",
  "/infrastructure/clients": "infrastructure",
  "/factory": "factory_management",
  "/factory/deleted-orders": "factory_management",
  "/factory/my-orders": "factory_orders",
  "/factory/orders": "factory_orders",
  "/marketing/exports": null,
};

const navItems = {
  admin: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/hr/dashboard", icon: Activity, label: "HR Dashboard" },
    { to: "/leads", icon: Users, label: "Leads" },
    { to: "/contacts", icon: Contact, label: "Contacts" },
    { to: "/sales-pipeline", icon: TrendingUp, label: "Sales Pipeline" },
    { to: "/products", icon: Package, label: "Products" },
    { to: "/companies", icon: Building2, label: "Companies" },
    { to: "/tasks", icon: ClipboardList, label: "Tasks" },
    { to: "/work-reports", icon: FileText, label: "Work Reports" },
    { to: "/teams", icon: UsersRound, label: "Teams" },
    { to: "/performance", icon: Award, label: "Performance" },
    { to: "/follow-ups", icon: CalendarClock, label: "Follow-ups" },
    { to: "/activities", icon: Zap, label: "Activities" },
    { to: "/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/reports", icon: FileText, label: "Reports" },
    { to: "/email-templates", icon: Mail, label: "Email Templates" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/users", icon: ShieldCheck, label: "Staff Management" },
    { to: "/integrations", icon: Plug, label: "Integrations" },
    { to: "/marketing", icon: Megaphone, label: "Marketing" },
    { to: "/admin/meta-lead-forms", icon: Facebook, label: "Meta Lead Forms" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/portal", icon: UserCircle, label: "My Portal" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
    { divider: true, label: "HR Module" },
    { to: "/hr/employees", icon: Briefcase, label: "HR Employees" },
    { to: "/hr/attendance", icon: CalendarCheck, label: "HR Attendance" },
    { to: "/hr/leaves", icon: CalendarClock, label: "HR Leaves" },
    { to: "/hr/payroll", icon: FileText, label: "HR Payroll" },
    { to: "/hr/payroll-engine", icon: Wallet, label: "Payroll Engine" },
    { to: "/hr/wages", icon: HardHat, label: "Factory Wages" },
    { to: "/hr/employee-user-link", icon: Link2, label: "Emp-User Linking" },
    { to: "/hr/hiring", icon: UserPlus, label: "HR Hiring" },
    { to: "/hr/reimbursements", icon: IndianRupee, label: "Reimbursements" },
    { to: "/hr/assets", icon: PackageIcon, label: "Assets Assigned" },
    { to: "/hr/announcements", icon: MegaphoneIcon, label: "Announcements" },
    { to: "/hr/doc-library", icon: FolderOpen, label: "Doc Library" },
    { to: "/hr/offboarding", icon: OffboardingIcon, label: "Offboarding" },
    { to: "/hr/analytics", icon: TrendingUp, label: "HR Analytics" },
    { to: "/hr/compliance", icon: ShieldCheck, label: "Compliance" },
    { to: "/hr/settings", icon: Settings, label: "HR Settings" },
    { divider: true, label: "Infrastructure" },
    { to: "/infrastructure", icon: HardHat, label: "Infra Dashboard" },
    { to: "/infrastructure/clients", icon: Warehouse, label: "Infra Clients" },
    { divider: true, label: "Factory" },
    { to: "/factory", icon: Factory, label: "Factory Management" },
    { to: "/factory/my-orders", icon: Package, label: "My Factory Orders" },
    { to: "/factory/deleted-orders", icon: Trash2, label: "Deleted Orders" },
  ],
  manager: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/quick-actions", icon: Smartphone, label: "Quick Actions" },
    { to: "/factory/my-orders", icon: Factory, label: "Factory Orders" },
    { to: "/sales-pipeline", icon: TrendingUp, label: "Sales Pipeline" },
    { to: "/leads", icon: Users, label: "Leads" },
    { to: "/contacts", icon: Contact, label: "Contacts" },
    { to: "/products", icon: Package, label: "Products" },
    { to: "/companies", icon: Building2, label: "Companies" },
    { to: "/tasks", icon: ClipboardList, label: "Tasks" },
    { to: "/work-reports", icon: FileText, label: "Work Reports" },
    { to: "/teams", icon: UsersRound, label: "My Teams" },
    { to: "/performance", icon: Award, label: "Performance" },
    { to: "/follow-ups", icon: CalendarClock, label: "Follow-ups" },
    { to: "/activities", icon: Zap, label: "Activities" },
    { to: "/email-templates", icon: Mail, label: "Email Templates" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/marketing", icon: Megaphone, label: "Marketing" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  telecaller: [
    { to: "/dashboard", icon: Phone, label: "My Dashboard" },
    { to: "/quick-actions", icon: Smartphone, label: "Quick Actions" },
    { to: "/factory/my-orders", icon: Factory, label: "Factory Orders" },
    { to: "/leads", icon: Users, label: "My Leads" },
    { to: "/contacts", icon: Contact, label: "Contacts" },
    { to: "/tasks", icon: ClipboardList, label: "Tasks" },
    { to: "/work-reports", icon: FileText, label: "Work Reports" },
    { to: "/follow-ups", icon: CalendarClock, label: "Follow-ups" },
    { to: "/activities", icon: Zap, label: "Activities" },
    { to: "/email-templates", icon: Mail, label: "Email Templates" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  sales: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/quick-actions", icon: Smartphone, label: "Quick Actions" },
    { to: "/factory/my-orders", icon: Factory, label: "Factory Orders" },
    { to: "/sales-pipeline", icon: TrendingUp, label: "Sales Pipeline" },
    { to: "/leads", icon: Users, label: "Leads" },
    { to: "/contacts", icon: Contact, label: "Contacts" },
    { to: "/products", icon: Package, label: "Products" },
    { to: "/companies", icon: Building2, label: "Companies" },
    { to: "/tasks", icon: ClipboardList, label: "Tasks" },
    { to: "/work-reports", icon: FileText, label: "Work Reports" },
    { to: "/follow-ups", icon: CalendarClock, label: "Follow-ups" },
    { to: "/activities", icon: Zap, label: "Activities" },
    { to: "/email-templates", icon: Mail, label: "Email Templates" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  hr: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/hr/dashboard", icon: Activity, label: "HR Dashboard" },
    { divider: true, label: "HR Module" },
    { to: "/hr/employees", icon: Briefcase, label: "HR Employees" },
    { to: "/hr/attendance", icon: CalendarCheck, label: "HR Attendance" },
    { to: "/hr/leaves", icon: CalendarClock, label: "HR Leaves" },
    { to: "/hr/payroll", icon: FileText, label: "HR Payroll" },
    { to: "/hr/payroll-engine", icon: Wallet, label: "Payroll Engine" },
    { to: "/hr/wages", icon: HardHat, label: "Factory Wages" },
    { to: "/hr/employee-user-link", icon: Link2, label: "Emp-User Linking" },
    { to: "/hr/hiring", icon: UserPlus, label: "HR Hiring" },
    { to: "/hr/reimbursements", icon: IndianRupee, label: "Reimbursements" },
    { to: "/hr/assets", icon: PackageIcon, label: "Assets Assigned" },
    { to: "/hr/announcements", icon: MegaphoneIcon, label: "Announcements" },
    { to: "/hr/doc-library", icon: FolderOpen, label: "Doc Library" },
    { to: "/hr/offboarding", icon: OffboardingIcon, label: "Offboarding" },
    { to: "/hr/analytics", icon: TrendingUp, label: "HR Analytics" },
    { to: "/hr/compliance", icon: ShieldCheck, label: "Compliance" },
    { to: "/hr/settings", icon: Settings, label: "HR Settings" },
    { divider: true, label: "Personal" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/portal", icon: UserCircle, label: "My Portal" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  junior_hr: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/hr/dashboard", icon: Activity, label: "HR Dashboard" },
    { divider: true, label: "HR Module" },
    { to: "/hr/employees", icon: Briefcase, label: "HR Employees" },
    { to: "/hr/attendance", icon: CalendarCheck, label: "HR Attendance" },
    { to: "/hr/leaves", icon: CalendarClock, label: "HR Leaves" },
    { to: "/hr/wages", icon: HardHat, label: "Factory Wages" },
    { to: "/hr/hiring", icon: UserPlus, label: "HR Hiring" },
    { to: "/hr/reimbursements", icon: IndianRupee, label: "Reimbursements" },
    { to: "/hr/assets", icon: PackageIcon, label: "Assets Assigned" },
    { to: "/hr/announcements", icon: MegaphoneIcon, label: "Announcements" },
    { to: "/hr/doc-library", icon: FolderOpen, label: "Doc Library" },
    { to: "/hr/offboarding", icon: OffboardingIcon, label: "Offboarding" },
    { to: "/hr/analytics", icon: TrendingUp, label: "HR Analytics" },
    { to: "/hr/settings", icon: Settings, label: "HR Settings" },
    { divider: true, label: "Personal" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/portal", icon: UserCircle, label: "My Portal" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  factory_manager: [
    { to: "/factory", icon: Factory, label: "Factory Dashboard" },
    { to: "/factory/my-orders", icon: Package, label: "My Factory Orders" },
    { to: "/factory/deleted-orders", icon: Trash2, label: "Deleted Orders" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  factory_supervisor: [
    { to: "/factory/supervisor-dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
  // Department-based nav (used when user's department matches)
  infrastructure_dept: [
    { to: "/infrastructure", icon: HardHat, label: "Infra Dashboard" },
    { to: "/infrastructure/clients", icon: Warehouse, label: "Infra Clients" },
    { to: "/leads", icon: Users, label: "Infra Leads" },
    { to: "/factory/my-orders", icon: Factory, label: "Factory Orders" },
    { to: "/tasks", icon: ClipboardList, label: "Tasks" },
    { to: "/work-reports", icon: FileText, label: "Work Reports" },
    { to: "/teams", icon: UsersRound, label: "Teams" },
    { to: "/performance", icon: Award, label: "Performance" },
    { to: "/follow-ups", icon: CalendarClock, label: "Follow-ups" },
    { to: "/activities", icon: Zap, label: "Activities" },
    { to: "/chat", icon: MessageSquare, label: "Chat" },
    { to: "/my-leaves", icon: CalendarDays, label: "My Leaves" },
    { to: "/attendance/checkin", icon: Clock, label: "Mobile Check-in" },
    { to: "/profile", icon: UserCircle, label: "My Profile" },
  ],
};

export default function Layout() {
  const { user, logout, hasPermission } = useAuth();
  const { lang, t, switchLanguage } = useLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Resolve effective role using template (handles custom roles by department).
  // Department-override: anyone in an Infrastructure department gets the
  // infrastructure_dept sidebar regardless of their built-in role (sales /
  // telecaller etc.), so Infra-dept staff land on Infra Dashboard + Infra Leads.
  const userDept = (user?.department || "").toLowerCase();
  const isInfraDept = userDept.includes("infra");
  const effectiveRole = isInfraDept && user?.role !== "admin" ? "infrastructure_dept" : roleTemplate(user);
  const rawItems = navItems[effectiveRole] || navItems[user?.role] || navItems.manager;

  // Filter nav items based on user permissions
  const items = rawItems.filter((item) => {
    if (item.divider) return true;
    if (!item.to) return true;
    const perm = routePermissionMap[item.to];
    if (perm === null || perm === undefined) return true; // No permission required (e.g., profile)
    return hasPermission(perm);
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "U";

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-[#114346] via-[#155155] to-[#0E3A3E] text-white flex flex-col transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] shadow-premium-lg ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="bg-white rounded-lg p-1.5 shrink-0">
            <img
              src={LOGO_URL}
              alt="Ameenji"
              className="h-8 w-auto"
            />
          </div>
          <div className="hidden md:block">
            <p className="text-xs text-[#FBBA00] font-heading font-semibold tracking-wider uppercase">
              CRM
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden ml-auto text-white hover:bg-white/10"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-btn"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-3 mb-2">
            <p className="text-[10px] text-slate-400 font-heading uppercase tracking-widest px-3 mb-2">
              {t("NAVIGATION")}
            </p>
          </div>
          {items.map((item, idx) => {
            if (item.divider) {
              return (
                <div key={`divider-${idx}`} className="px-3 mt-4 mb-2">
                  <Separator className="bg-white/10 mb-2" />
                  <p className="text-[10px] text-[#FBBA00]/70 font-heading uppercase tracking-widest px-3">
                    {t(item.label)}
                  </p>
                </div>
              );
            }
            return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? "active" : ""} flex items-center gap-3 px-4 py-2.5 mx-3 rounded-md text-sm font-medium mb-0.5 ${
                  isActive
                    ? "bg-[#FBBA00] text-[#155155] font-semibold shadow-sm"
                    : "text-slate-300 hover:text-white hover:bg-white/10"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon size={18} />
              <span>{t(item.label)}</span>
            </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-white/10 p-4">
          {/* Language Toggle */}
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">Language</span>
            <div className="flex bg-white/10 rounded-md p-0.5">
              <button
                className={`px-2.5 py-1 text-[10px] font-semibold rounded transition-colors ${lang === "en" ? "bg-[#FBBA00] text-[#155155]" : "text-slate-400 hover:text-white"}`}
                onClick={() => switchLanguage("en")}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button
                className={`px-2.5 py-1 text-[10px] font-semibold rounded transition-colors ${lang === "hi" ? "bg-[#FBBA00] text-[#155155]" : "text-slate-400 hover:text-white"}`}
                onClick={() => switchLanguage("hi")}
                data-testid="lang-hi-btn"
              >
                हिंदी
              </button>
            </div>
          </div>
          <div
            className="flex items-center gap-3 mb-3 cursor-pointer hover:bg-white/5 rounded-md p-1 -m-1 transition-colors"
            onClick={() => {
              navigate("/profile");
              setSidebarOpen(false);
            }}
            data-testid="sidebar-profile-link"
          >
            <Avatar className="h-9 w-9 bg-[#FBBA00]">
              <AvatarFallback className="bg-[#FBBA00] text-[#155155] font-heading font-bold text-sm">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10 text-sm"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            {t("Sign Out")}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-14 border-b border-slate-200 bg-white/80 backdrop-blur-md flex items-center px-4 md:px-6 shrink-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden mr-2"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-toggle-btn"
          >
            <Menu size={20} />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <NotificationBell />
            <span className="text-xs text-slate-500 font-body hidden sm:block">
              {user?.email}
            </span>
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom-nav — visible only on small screens, hides md+ */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 md:hidden flex justify-around items-stretch h-16 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {[
          { to: "/dashboard", icon: LayoutDashboard, label: "Home", perm: null, testid: "mob-nav-home" },
          { to: "/attendance/checkin", icon: Clock, label: "Check-in", perm: "mobile_checkin", testid: "mob-nav-checkin" },
          { to: "/my-leaves", icon: CalendarDays, label: "Leaves", perm: "my_leaves", testid: "mob-nav-leaves" },
          { to: "/chat", icon: MessageSquare, label: "Chat", perm: "chat", testid: "mob-nav-chat" },
          { to: "/profile", icon: UserCircle, label: "Profile", perm: null, testid: "mob-nav-profile" },
        ]
          .filter((b) => !b.perm || hasPermission(b.perm))
          .map((b) => (
            <NavLink
              key={b.to}
              to={b.to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
                  isActive ? "text-[#155155]" : "text-slate-500 active:text-[#155155]"
                }`
              }
              data-testid={b.testid}
            >
              {({ isActive }) => (
                <>
                  <b.icon className={`w-5 h-5 transition-transform ${isActive ? "scale-110" : ""}`} strokeWidth={isActive ? 2.5 : 2} />
                  <span className="leading-tight">{b.label}</span>
                  {isActive && <span className="h-0.5 w-6 bg-[#155155] rounded-full -mt-0.5" />}
                </>
              )}
            </NavLink>
          ))}
      </nav>

      <PushNotificationPrompt />
    </div>
  );
}
